import axios from "axios";
import reactotron from "ReactotronConfig";
//import basic from 'basic-authorization-header';

import { sec } from "utils/security";

const CSV_CHECK_TIMEOUT_MS = 60000;
const CSV_VALIDATE_TIMEOUT_MS = 60000;
const VEHICLES_TIMEOUT_MS = 60000;
const TRANSPORT_TASKS_TIMEOUT_MS = 60000;
const DRIVER_CONVERSATIONS_TIMEOUT_MS = 60000;
const DRIVER_REGISTRY_TIMEOUT_MS = 60000;
const BUSINESS_RULES_TIMEOUT_MS = 60000;

function deriveDriverRegistryEndpoint(backendEndpoint) {
  const configured = process.env.REACT_APP_DRIVER_REGISTRY_API_ENDPOINT;
  if (configured) return configured.replace(/\/+$/, "");
  const backend = (backendEndpoint || "").replace(/\/+$/, "");
  if (!backend) return "/driver-registry";
  if (/\/api$/i.test(backend)) return backend.replace(/\/api$/i, "/driver-registry");
  return `${backend}/driver-registry`;
}

const backendAPI = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_API_ENDPOINT || "/api",
  // baseURL: 'http://localhost:3001',
  timeout: 30000,
});

const driverRegistryAPI = axios.create({
  baseURL: deriveDriverRegistryEndpoint(process.env.REACT_APP_BACKEND_API_ENDPOINT),
  timeout: DRIVER_REGISTRY_TIMEOUT_MS,
});

function extractResponseMetadata(response) {
  return {
    data: response.data,
    etag: response.headers.etag || null,
    contractVersion: response.headers["x-transport-task-contract-version"] || null,
    conversationContractVersion: response.headers["x-driver-conversation-contract-version"] || null,
    businessRulesContractVersion: response.headers["x-business-rules-contract-version"] || null,
  };
}

function reportAPIError(error) {
  // Keep production diagnostics useful without logging tokens, response bodies
  // or personal data. Authentication failures often happen before a request
  // reaches FleetAdmin, so the browser console is the only available evidence.
  console.error("FleetAdmin API request failed " + JSON.stringify({
    name: error && error.name,
    code: error && (error.errorCode || error.code),
    subError: error && error.subError,
    status: error && error.response && error.response.status,
    path: error && error.config && error.config.url,
  }));
}

//Interceptors
//Inject JWT Access Token to each request
function installAuthorizationInterceptor(client) {
  client.interceptors.request.use(
  async (config) => {
    const tokenProvider = sec.getAccessToken();
    if (!tokenProvider) throw new Error("Entra token provider is not initialized");
    const token = await tokenProvider();
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
  );
}

installAuthorizationInterceptor(backendAPI);
installAuthorizationInterceptor(driverRegistryAPI);
//return data from each response and reject in case of error which will trigger ModalError
backendAPI.interceptors.response.use(
  (response) => {
    reactotron.logImportant('backendAPI', response.config.url, response)
    if (response.config.includeResponseMetadata) {
      return extractResponseMetadata(response);
    }
    return response.data;
  },
  function (error) {
    reportAPIError(error);
    return Promise.reject(error);
  }
);

driverRegistryAPI.interceptors.response.use(
  response => response.data,
  error => {
    reportAPIError(error);
    return Promise.reject(error);
  },
);

async function checkCSV(fileData) {
  let formData = new FormData();
  formData.append(fileData.fileName, fileData.content);
  let headers = {
    "Content-Type": "multipart/form-data",
  };
  return backendAPI.post("/csv/check", formData, {
    headers,
    timeout: CSV_CHECK_TIMEOUT_MS,
  });
}

async function validateCSV(fileName) {
  return backendAPI.get("/csv/validate/" + fileName, {
    timeout: CSV_VALIDATE_TIMEOUT_MS,
  });
}

async function getVehicles() {
  return backendAPI.get("/vehicles", {
    timeout: VEHICLES_TIMEOUT_MS,
  });
}

async function getCompanies() {
  return backendAPI.get("/manualcostcompanies");
}

async function getLocations() {
  return backendAPI.get("/manualcostlocations");
}

async function getCostTypes() {
  return backendAPI.get("/manualcosttypes");
}

async function addCost(costdata) {
  return backendAPI.post("/cost", costdata);
}

async function getUnlinkedRevenues() {
  return backendAPI.get("/unlinkedrevenues");
}

async function linkVehicleToRevenue(linkdata) {
  return backendAPI.post(
    `/vehicle/${linkdata.vehicle_id}/link`,
    linkdata.revenue_id
  );
}

async function getRevenueTypes() {
  return backendAPI.get("/manualrevenuetypes");
}

async function addRevenue(revenuedata) {
  return backendAPI.post("/revenue", revenuedata);
}

async function getLatestCosts() {
  return backendAPI.get("/latestmanualcosts");
}

async function deleteCost(id) {
  return backendAPI.delete("/cost/" + id);
}

async function getLatestRevenues() {
  return backendAPI.get("/latestmanualrevenues");
}

async function deleteRevenue(id) {
  return backendAPI.delete("/revenue/" + id);
}

async function getCostGroups() {
  return backendAPI.get("/costgroups");
}

async function getRevenueGroups() {
  return backendAPI.get("/revenuegroups");
}

async function getUngroupedCostTypes() {
  return backendAPI.get("/costtypes/ungrouped");
}
async function getUngroupedRevenueTypes() {
  return backendAPI.get("/revenuetypes/ungrouped");
}

async function linkCosttypesToGroup(linkdata) {
  return backendAPI.post(
    `/costtypes/${linkdata.group_id}/link`,
    linkdata.costtypes
  );
}

async function linkRevenuetypesToGroup(linkdata) {
  return backendAPI.post(
    `/revenuetypes/${linkdata.group_id}/link`,
    linkdata.revenuetypes
  );
}

// Service fleet APIs
async function getVehicleServiceData() {
  return backendAPI.get("/servicevehicles");
}

async function updateVehicleServiceData(servicedata) {
  return backendAPI.put("/servicevehicles", servicedata);
}

async function getVehicleServiceHistory(vin) {
  return backendAPI.get(`/serviceevent/${vin}`);
}

async function getVehicleServiceAxels(id) {
  return backendAPI.get(`/serviceaxels/${id}`);
}

async function updateVehicleService(id, data) {
  return backendAPI.put(`/servicevehicles/${id}`, data)
}

async function addService(id, data) {
  return backendAPI.post(`/serviceevent/${id}`, data)
}

async function getViewServices(id) {
  return backendAPI.get(`/serviceevent/${id}`)
}

async function deleteViewServiceById(id) {
  return backendAPI.delete(`/serviceevent/${id}`)
}

// Transport task APIs are intentionally read-only in the first TMS release.
// The detail response retains the server ETag so the UI can expose the exact
// authoritative version without creating a client-side business state.
async function getTransportTasks() {
  return backendAPI.get("/ai/transport-tasks", {
    params: {
      status: "draft,planned,active,blocked,completed,cancelled",
      limit: 200,
    },
    includeResponseMetadata: true,
    timeout: TRANSPORT_TASKS_TIMEOUT_MS,
  });
}

async function getTransportTask(id) {
  return backendAPI.get(`/ai/transport-tasks/${encodeURIComponent(id)}`, {
    includeResponseMetadata: true,
    timeout: TRANSPORT_TASKS_TIMEOUT_MS,
  });
}

export const getTaskMapPlan = id => backendAPI.get(`/ai/transport-tasks/${encodeURIComponent(id)}/map-plan`, { timeout: 60000 });
export const getTaskMapBackground = (id, viewport) => backendAPI.get(`/ai/transport-tasks/${encodeURIComponent(id)}/map-background`, { params: viewport, responseType: "blob" });
export const getTaskMapAttribution = (id, viewport) => backendAPI.get(`/ai/transport-tasks/${encodeURIComponent(id)}/map-attribution`, { params: viewport });
export const getMapVehicles = () => backendAPI.get("/vehiclelocations", { params: { limit: 500 } });
export const getMapHistory = (vehicle, params) => backendAPI.get(`/vehiclelocations/${vehicle}/history`, { params });

// The two projections are loaded together: masked context carries assignment
// audit data, while the no-store directory supplies operator-visible contact
// values for data verification in the authenticated portal.
async function getDrivers() {
  return driverRegistryAPI.get("/v1/drivers", {
    timeout: DRIVER_REGISTRY_TIMEOUT_MS,
  });
}

async function getTransportDocuments(taskId) {
  return backendAPI.get("/ai/transport-tasks/" + encodeURIComponent(taskId) + "/documents", {
    timeout: TRANSPORT_TASKS_TIMEOUT_MS,
  });
}

async function downloadTransportDocument(taskId, documentId) {
  return backendAPI.get("/ai/transport-tasks/" + encodeURIComponent(taskId) + "/documents/" + encodeURIComponent(documentId) + "/content", {
    responseType: "blob",
    timeout: TRANSPORT_TASKS_TIMEOUT_MS,
  });
}

async function getDriverDirectory() {
  return driverRegistryAPI.get("/v1/directory", {
    timeout: DRIVER_REGISTRY_TIMEOUT_MS,
  });
}

async function getDriverConversations(day) {
  return backendAPI.get("/ai/driver-conversations", {
    params: { day },
    includeResponseMetadata: true,
    timeout: DRIVER_CONVERSATIONS_TIMEOUT_MS,
  });
}

async function getBusinessRules(includeHistory = true) {
  return backendAPI.get("/ai/business-rules", {
    params: { include_history: includeHistory },
    includeResponseMetadata: true,
    timeout: BUSINESS_RULES_TIMEOUT_MS,
  });
}

export const getReportQueue = () => backendAPI.get("/ai/report-queue", { timeout: 20000 });
export const updateReportProfile = (profile) => backendAPI.put("/ai/report-queue/profiles/" + encodeURIComponent(profile.group), profile, { timeout: 20000 });
export const decideReport = (report, action, reason = "") => backendAPI.post("/ai/report-queue/" + encodeURIComponent(report.id) + "/" + action, {
  group: report.group, expected_version: report.version, digest: report.digest, reason,
}, { timeout: 20000 });

export {
  extractResponseMetadata,
  checkCSV,
  validateCSV,
  getVehicles,
  getCompanies,
  getLocations,
  getCostTypes,
  addCost,
  getUnlinkedRevenues,
  linkVehicleToRevenue,
  getRevenueTypes,
  addRevenue,
  getLatestCosts,
  getLatestRevenues,
  deleteCost,
  deleteRevenue,
  getCostGroups,
  getRevenueGroups,
  getUngroupedCostTypes,
  getUngroupedRevenueTypes,
  linkCosttypesToGroup,
  linkRevenuetypesToGroup,
  getVehicleServiceData,
  updateVehicleServiceData,
  getVehicleServiceHistory,
  getVehicleServiceAxels,
  updateVehicleService, addService,
  getViewServices,
  deleteViewServiceById,
  getTransportTasks,
  getTransportTask,
  getTransportDocuments,
  downloadTransportDocument,
  getDrivers,
  getDriverDirectory,
  getDriverConversations,
  getBusinessRules,
  deriveDriverRegistryEndpoint
};
